import LoadingScreen from '@/common/LoadingScreen';
import { useAuthUser } from '@/hooks/useAuthUser';
import useCurrentUser from '@/hooks/useCurrentUser';
import { firebaseAuth } from '@/lib/Firebase';
import { useQueryClient } from '@tanstack/react-query';
import { NextComponentType } from 'next';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

function withAuth(Component: NextComponentType) {
  const Auth = (props: any) => {
    const router = useRouter();
    const { isLoading: isAuthLoading, data: authUser } = useAuthUser(['authUser'], firebaseAuth);
    const {
      data: user,
      isLoading: isUserLoading,
      isError: isUserError,
    } = useCurrentUser(!!authUser);
    const queryClient = useQueryClient();

    useEffect(() => {
      const unsubscribe = firebaseAuth.onAuthStateChanged((firebaseUser) => {
        !authUser && firebaseUser?.reload();
        if (!firebaseUser) {
          queryClient.invalidateQueries({ queryKey: ['authUser'] });
          queryClient.clear();
          const path = router.pathname ?? '';
          if (!path.endsWith('/login')) {
            router.replace('/login');
          }
        }
      });

      return () => unsubscribe();
    }, [authUser, queryClient, router]);

    useEffect(() => {
      if (!authUser || isUserLoading || isUserError) return;
      if (user?.isOnboardingComplete) return;
      if (router.pathname?.startsWith('/onboarding')) return;
      router.replace('/onboarding');
    }, [authUser, isUserLoading, isUserError, user, router]);

    // Wait for auth check
    if (isAuthLoading && !authUser) {
      return <LoadingScreen />;
    }

    // Wait for user data to load once authenticated
    if (authUser && isUserLoading) {
      return <LoadingScreen />;
    }

    // Onboarding incomplete or no profile yet — loading while redirect runs (or stuck if user query errors)
    if (authUser && !isUserLoading && !user?.isOnboardingComplete) {
      return <LoadingScreen />;
    }

    // @ts-ignore - TypeScript compatibility issue with React 18 types
    return <Component {...props} />;
  };

  // Copy getInitial props so it will run as well
  if (Component.getInitialProps) {
    Auth.getInitialProps = Component.getInitialProps;
  }

  return Auth;
}

export default withAuth;
